import { Component, Fragment, h, Host, Prop, State, Watch, Element } from '@stencil/core';
import { LabelParser } from '../sew-ws-header/helpers/labels.helpers';
import { Labels } from '../sew-ws-header/types/labels.types';
import { fetchFooterData, fetchLabels, fetchNewestBlogPostData } from './sew-ws-footer-api.service';
import { BlogPostResponse, FooterData } from './sew-ws-footer-api.types';
import { convertToPlaintext, truncateWholeWords } from './sew-ws-footer.helper';

@Component({
    tag: 'sew-ws-footer-api',
    styleUrl: 'sew-ws-footer-api.scss',
    shadow: true,
})
export class SewWsFooterApi {
    @Element() host: HTMLSewWsFooterApiElement;
    @Prop() language: string;
    @Prop() legalOnly?: boolean;
    @Prop() noSocial?: boolean;
    @Prop() noMargin?: boolean;
    @Prop() noLogo?: boolean;

    /**
     * ID of the CMS Blog Project.
     * This value is the same for blog and country cms project (de for SEWDE and Blog-DE).
     *
     * @example de
     * @example uk
     */
    @Prop() countryCode?: string;

    /**
     * Base url for caas requests.
     * This value is needed to fetch data from CaaS.
     *
     * @example https://caas.sew-eurodrive.com
     */
    @Prop() caasBaseUrl?: string;

    /**
     * Determines if the latest blog post is displayed or not
     *
     * @default false
     */
    @Prop() displayNewestBlogPost = false;

    @State() footerData: FooterData | null;
    @State() blogPostData: BlogPostResponse | null;
    @State() svgMarkup: string;
    @State() labels: Labels;

    private labelParser: LabelParser;

    @Watch('language')
    updateLanguage(): void {
        this.getFooterData();
    }

    @Watch('caasBaseUrl')
    updateCaasBaseUrl(): void {
        this.getFooterData();
    }

    private copyright: string;

    private getFooterData(): void {
        if (this.allRequiredDataIsSet()) {
            fetchFooterData(this.language, this.caasBaseUrl, this.countryCode)
                .then(res => (this.footerData = res))
                .then(this.getLogoMarkup);

            this.displayNewestBlogPost && this.getBlogPostData();
        }
    }

    private getBlogPostData(): void {
        fetchNewestBlogPostData(this.language, this.caasBaseUrl, this.countryCode).then(post => {
            this.blogPostData = post;
        });

        fetchLabels(this.language, this.caasBaseUrl, this.countryCode)
            .then(labelResponse => {
                if (labelResponse === null) {
                    return null;
                }
                return labelResponse.dictionary;
            })
            .then(labels => (this.labelParser = new LabelParser(labels)));
    }

    private getLogoMarkup = async (): Promise<void> => {
        if (this.footerData === null) {
            return;
        }

        await fetch(this.footerData.logo.logo + '?SEWNoSSO=true')
            .then(
                svg => svg.text(),
                err => console.error(err)
            )
            .then(text => (this.svgMarkup = text as string))
            .catch(err => console.error(err));
    };

    private allRequiredDataIsSet(): boolean {
        return (
            this.countryCode !== undefined &&
            this.countryCode.length > 0 &&
            this.caasBaseUrl !== undefined &&
            this.caasBaseUrl.length > 0
        );
    }

    private renderFourthColumn = () => {
        return (
            <sew-ws-footer-teaser
                slot="column4"
                headline={this.footerData.footerSectionFourthColumn.headline}
            >
                <sew-picture
                    img-src={this.footerData.footerSectionFourthColumn.image.src}
                    img-alt={this.footerData.footerSectionFourthColumn.image.alt}
                    img-title={this.footerData.footerSectionFourthColumn.image.title}
                ></sew-picture>
                <p>{this.footerData.footerSectionFourthColumn.description}</p>
                <sew-ws-link>
                    <a href={this.footerData.footerSectionFourthColumn.link.href}>
                        {this.footerData.footerSectionFourthColumn.link.text}
                    </a>
                </sew-ws-link>
            </sew-ws-footer-teaser>
        );
    };

    private renderFourthColumnWithBlogPost = () => {
        return (
            <sew-ws-footer-teaser slot="column4" headline={this.blogPostData.headline}>
                <a href={this.blogPostData.url} class="blog-post">
                    <sew-picture
                        img-src={this.blogPostData.preview_picture.mq1}
                        img-alt={this.blogPostData.hero_picture_alt}
                        img-title={this.blogPostData.hero_picture_title}
                    >
                        <source
                            srcSet={
                                this.blogPostData.preview_picture.mq1 +
                                ' 1x, ' +
                                this.blogPostData.preview_picture.mq1r +
                                ' 2x'
                            }
                        />
                    </sew-picture>
                </a>
                <p
                    innerHTML={truncateWholeWords(
                        convertToPlaintext(this.blogPostData.content[0].text),
                        120,
                        '...'
                    )}
                ></p>
                <sew-ws-link>
                    <a href={this.blogPostData.url}>
                        {this.labelParser && this.labelParser.getLabel('CMS_FOOTER_READ_BLOG_POST')}
                    </a>
                </sew-ws-link>
            </sew-ws-footer-teaser>
        );
    };

    private setFooterMargin() {
        const gsapElement = document.querySelector('sew-ws-gsap');

        if (!gsapElement) return;

        const gsapChildren = gsapElement.children;

        if (gsapChildren.length === 0) return;

        let lastElementInGsap = gsapChildren[gsapChildren.length - 1];

        if (lastElementInGsap.classList.contains('pin-spacer')) {
            lastElementInGsap = lastElementInGsap.children[0];
        }

        if (
            lastElementInGsap.matches(
                'sew-ws-report-pre-registration, sew-ws-outro, sew-ws-fullscreen-teaser, sew-ws-image-text[underlay], sew-ws-circle-teaser2'
            )
        ) {
            this.host.style.setProperty('--footer-margin-top', '1px');
        }
    }

    componentWillLoad(): void {
        this.setFooterMargin();
        this.copyright = `© ${new Date().getFullYear()} SEW-EURODRIVE`;
        this.getFooterData();
    }

    render() {
        return (
            <Host>
                <sew-ws-footer
                    class={{ 'legal-only': this.legalOnly }}
                    noMargin={this.noMargin}
                    copyright={this.copyright}
                    logo={!this.noLogo && this.legalOnly}
                >
                    {!this.legalOnly && (
                        <Fragment>
                            {this.footerData && this.footerData.footerSectionFirstColumn.length > 0 && (
                                <sew-ws-footer-linklist
                                    slot="column1"
                                    headline={this.footerData.footerSectionFirstColumn[0].headline}
                                >
                                    {this.footerData.footerSectionFirstColumn.map(row => (
                                        <sew-ws-footer-link>
                                            <a href={row.link.href}>{row.link.text}</a>
                                        </sew-ws-footer-link>
                                    ))}
                                </sew-ws-footer-linklist>
                            )}
                            {this.footerData && this.footerData.footerSectionSecondColumn.length > 0 && (
                                <sew-ws-footer-linklist
                                    slot="column2"
                                    headline={this.footerData.footerSectionSecondColumn[0].headline}
                                >
                                    {this.footerData.footerSectionSecondColumn.map(row => (
                                        <sew-ws-footer-link>
                                            <a href={row.link.href}>{row.link.text}</a>
                                        </sew-ws-footer-link>
                                    ))}
                                </sew-ws-footer-linklist>
                            )}
                            {this.footerData && this.footerData.contact && (
                                <sew-ws-footer-phone
                                    slot="column2"
                                    headline={this.footerData.contact.headline}
                                >
                                    <a href={'tel:' + this.footerData.contact.phone}>
                                        {this.footerData.contact.phone}
                                    </a>
                                </sew-ws-footer-phone>
                            )}
                            {this.footerData && this.footerData.footerSectionThirdColumn.length > 0 && (
                                <sew-ws-footer-linklist
                                    slot="column3"
                                    headline={this.footerData.footerSectionThirdColumn[0].headline}
                                >
                                    {this.footerData.footerSectionThirdColumn.map(row => (
                                        <sew-ws-footer-link>
                                            <a href={row.link.href}>{row.link.text}</a>
                                        </sew-ws-footer-link>
                                    ))}
                                </sew-ws-footer-linklist>
                            )}
                            {this.blogPostData && this.renderFourthColumnWithBlogPost()}
                            {!this.blogPostData &&
                                this.footerData &&
                                this.footerData.footerSectionFourthColumn &&
                                this.renderFourthColumn()}
                        </Fragment>
                    )}

                    {!this.noLogo &&
                        this.legalOnly &&
                        this.footerData &&
                        this.footerData.logo &&
                        this.svgMarkup && (
                            <a
                                slot="logo"
                                href={this.footerData.logo.link.href}
                                class={this.footerData.logo.class}
                                title={this.footerData.logo.title}
                                innerHTML={this.svgMarkup}
                            />
                        )}

                    {this.footerData && this.footerData.bottomLinks.length > 0 && (
                        <sew-ws-footer-linklist-legal slot="legal">
                            {this.footerData.bottomLinks.map(col => (
                                <a href={col.link.href}>{col.link.text}</a>
                            ))}
                            <slot name="additional-legal-links"></slot>
                        </sew-ws-footer-linklist-legal>
                    )}
                    {!this.noSocial && this.footerData && this.footerData.socialLinks.length > 0 && (
                        <sew-ws-footer-linklist-social slot="social">
                            {this.footerData.socialLinks.map(link => (
                                <a href={link.href} aria-label={link.text}>
                                    <sew-icon icon={'logo-' + link.type.toLowerCase()}></sew-icon>
                                </a>
                            ))}
                        </sew-ws-footer-linklist-social>
                    )}
                </sew-ws-footer>
            </Host>
        );
    }
}
